import { useEffect } from "react";
import { UseFormSetValue, UseFormReset } from "react-hook-form";

/**
 * Field transformation function type
 */
type FieldTransformer = (value: string) => string | Record<string, string>;

/**
 * Field mapping configuration
 */
interface FieldMapping {
  queryParam: string | string[]; // Single param or array of params to combine
  formField: string;
  transform?: FieldTransformer;
}

/**
 * Common field transformers
 */
const transformers = {
  /**
   * Combines multiple query parameters into a single value (e.g., firstName + middleName + lastName)
   */
  combineNames: (values: string[]): string => {
    return values.filter(v => v).join(" ").trim();
  },

  /**
   * Converts international phone format to local format
   * E.g., +2348123456789 or 2348123456789 -> 08123456789
   */
  toLocalPhone: (value: string): string => {
    if (value.startsWith("+234")) {
      return "0" + value.substring(4);
    } else if (value.startsWith("234")) {
      return "0" + value.substring(3);
    }
    return value;
  },

  /**
   * Splits international phone into phoneCode and phone
   * E.g., "+2348123456789" -> { phoneCode: "+234", phone: "8123456789" }
   * E.g., "2348123456789" -> { phoneCode: "+234", phone: "8123456789" }
   */
  splitPhone: (value: string): Record<string, string> => {
    if (!value) {
      return { phoneCode: "+261", phone: "" };
    }
    
    // If already has country code format (+261... or 261...)
    if (value.startsWith("+")) {
      return {
        phoneCode: value.substring(0, 4),
        phone: value.substring(4)
      };
    } else if (value.length >= 12) {
      // Likely has country code without + (e.g., 261340123456)
      return {
        phoneCode: `+${value.substring(0, 3)}`,
        phone: value.substring(3)
      };
    } else {
      // Local number without country code, default to Madagascar (+261)
      return {
        phoneCode: "+261",
        phone: value
      };
    }
  },

  /**
   * Capitalizes first letter (e.g., "male" -> "Male")
   */
  capitalize: (value: string): string => {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  },

  /**
   * Identity transformer (returns value as-is)
   */
  identity: (value: string): string => value,

  /**
   * Normalizes education level to match exact dropdown values (case-insensitive)
   * E.g., "tertiary" -> "Tertiary", "phd" -> "PhD", "masters" -> "Masters"
   */
  normalizeEducation: (value: string): string => {
    const normalized = value.toLowerCase();
    const educationMap: Record<string, string> = {
      "primary": "Primary",
      "secondary": "Secondary",
      "tertiary": "Tertiary",
      "masters": "Masters",
      "phd": "PhD",
      "ph.d.": "PhD",
      "ph.d": "PhD"
    };
    return educationMap[normalized] || value;
  },

  /**
   * Normalizes marital status to match exact dropdown values (case-insensitive)
   * E.g., "Single" -> "single", "MARRIED" -> "married"
   */
  normalizeMaritalStatus: (value: string): string => {
    const normalized = value.toLowerCase();
    const statusMap: Record<string, string> = {
      "single": "single",
      "married": "married",
      "engaged": "engaged",
      "divorced": "divorced",
      "widowed": "widowed",
      "notavailable": "notAvailable",
      "not available": "notAvailable",
      "not_available": "notAvailable"
    };
    return statusMap[normalized] || value;
  },

  /**
   * Normalizes gender to match exact dropdown values (case-insensitive)
   * E.g., "Male" -> "male", "FEMALE" -> "female"
   */
  normalizeGender: (value: string): string => {
    const normalized = value.toLowerCase();
    const genderMap: Record<string, string> = {
      "male": "male",
      "female": "female",
      "prefernottosay": "preferNotToSay",
      "prefer not to say": "preferNotToSay",
      "prefer_not_to_say": "preferNotToSay"
    };
    return genderMap[normalized] || value;
  },

  /**
   * Normalizes WhatsApp type to match exact dropdown values (case-insensitive)
   * E.g., "personal" -> "PERSONAL", "business" -> "BUSINESS"
   */
  normalizeWhatsappType: (value: string): string => {
    const normalized = value.toLowerCase();
    const typeMap: Record<string, string> = {
      "personal": "PERSONAL",
      "business": "BUSINESS",
      "personalandbusiness": "PERSONALANDBUSINESS",
      "personal and business": "PERSONALANDBUSINESS",
      "personal_and_business": "PERSONALANDBUSINESS"
    };
    return typeMap[normalized] || value;
  },

  /**
   * Normalizes TIN type to match exact dropdown values (case-insensitive)
   * E.g., "personal" -> "PERSONAL", "none" -> "NONE"
   */
  normalizeTinType: (value: string): string => {
    const normalized = value.toLowerCase();
    const typeMap: Record<string, string> = {
      "none": "NONE",
      "personal": "PERSONAL",
      "business": "BUSINESS"
    };
    return typeMap[normalized] || value;
  },

  /**
   * Normalizes Yes/No values (case-insensitive)
   * E.g., "yes" -> "Yes", "NO" -> "No"
   */
  normalizeYesNo: (value: string): string => {
    const normalized = value.toLowerCase();
    if (normalized === "yes" || normalized === "true" || normalized === "1") {
      return "Yes";
    }
    if (normalized === "no" || normalized === "false" || normalized === "0") {
      return "No";
    }
    return value;
  },

  /**
   * Normalizes housing situation text (case-insensitive, handles underscores and spaces)
   * E.g., "Living With Parents" -> "living_with_parents"
   */
  normalizeHousingSituation: (value: string): string => {
    const normalized = value.toLowerCase().replace(/\s+/g, "_");
    const housingMap: Record<string, string> = {
      "living_with_parents": "living_with_parents",
      "livingwithparents": "living_with_parents",
      "living_with_family_members": "living_with_family_members",
      "livingwithfamilymembers": "living_with_family_members",
      "own_house": "own_house",
      "ownhouse": "own_house",
      "own_apartment": "own_apartment",
      "ownapartment": "own_apartment",
      "rent_house": "rent_house",
      "renthouse": "rent_house",
      "rent_apartment": "rent_apartment",
      "rentapartment": "rent_apartment"
    };
    return housingMap[normalized] || value;
  },

  /**
   * Normalizes cell phone usage period text (case-insensitive, handles underscores and spaces)
   * E.g., "Less Than 3 Months" -> "less_than_3_months"
   */
  normalizeCellPhonePeriod: (value: string): string => {
    const normalized = value.toLowerCase().replace(/\s+/g, "_");
    const periodMap: Record<string, string> = {
      "less_than_3_months": "less_than_3_months",
      "lessthan3months": "less_than_3_months",
      "less_than_6_months_more_than_3": "less_than_6_months_more_than_3",
      "lessthan6monthsmorethan3": "less_than_6_months_more_than_3",
      "less_than_1_year_more_than_6_months": "less_than_1_year_more_than_6_months",
      "lessthan1yearmorethan6months": "less_than_1_year_more_than_6_months",
      "one_to_two_years": "one_to_two_years",
      "onetotwoyears": "one_to_two_years",
      "1_to_2_years": "one_to_two_years",
      "two_to_three_years": "two_to_three_years",
      "twotothreeyears": "two_to_three_years",
      "2_to_3_years": "two_to_three_years",
      "three_to_four_years": "three_to_four_years",
      "threetofouryears": "three_to_four_years",
      "3_to_4_years": "three_to_four_years",
      "five_years_and_more": "five_years_and_more",
      "fiveyearsandmore": "five_years_and_more",
      "5_years_and_more": "five_years_and_more"
    };
    return periodMap[normalized] || value;
  },
};

/**
 * Prepopulates form fields from URL query parameters using a declarative mapping configuration.
 * Eliminates repetitive if statements by using a data-driven approach.
 *
 * @param searchParams - URLSearchParams from useSearchParams
 * @param setValue - setValue function from react-hook-form
 * @param fieldMappings - Array of field mapping configurations
 */
export const useFormPrepopulation = (
  searchParams: URLSearchParams,
  setValue: UseFormSetValue<any>,
  fieldMappings: FieldMapping[]
) => {
  useEffect(() => {
    fieldMappings.forEach(({ queryParam, formField, transform = transformers.identity }) => {
      // Handle combined fields (e.g., fullName from firstName + middleName + lastName)
      if (Array.isArray(queryParam)) {
        const values = queryParam.map(param => searchParams.get(param) || "");
        const hasAnyValue = values.some(v => v);

        if (hasAnyValue) {
          const combinedValue = transformers.combineNames(values);
          if (combinedValue) {
            setValue(formField, combinedValue);
          }
        }
      } else {
        // Handle single field mapping
        const value = searchParams.get(queryParam);
        if (value) {
          setValue(formField, transform(value));
        }
      }
    });
  }, [searchParams, setValue, fieldMappings]);
};

/**
 * Predefined field mappings for BuildAfrica form
 */
export const buildAfricaFieldMappings: FieldMapping[] = [
  // Personal Information
  { queryParam: ["firstName", "middleName", "lastName"], formField: "fullName" },
  { queryParam: "email", formField: "email" },
  { queryParam: "phoneNo", formField: "phone", transform: transformers.toLocalPhone },
  { queryParam: "dateOfBirth", formField: "dob" },
  { queryParam: "maritalStatus", formField: "maritalStatus", transform: transformers.capitalize },
  { queryParam: "gender", formField: "gender", transform: transformers.capitalize },
  { queryParam: "address", formField: "address" },

  // Next of Kin
  { queryParam: "nextOfKinName", formField: "nextOfKin" },
  { queryParam: "nextOfKinPhone", formField: "nextOfKinPhone", transform: transformers.toLocalPhone },

  // Business Information
  { queryParam: "businessName", formField: "businessName" },
  { queryParam: "businessDescription", formField: "businessNature" },
  { queryParam: "businessAddress", formField: "businessAddress" },

  // TIN Information
  { queryParam: "tinNumber", formField: "tin" },
  { queryParam: "tinType", formField: "tinType", transform: transformers.normalizeTinType },

  // Loan Information
  { queryParam: "amount", formField: "loanAmount" },
];

/**
 * Predefined field mappings for Drugstoc form
 */
export const drugstocFieldMappings: FieldMapping[] = [
  // Personal Information
  { queryParam: ["firstName", "middleName", "lastName"], formField: "fullName" },
  { queryParam: "email", formField: "email" },
  { queryParam: "phoneNo", formField: "phone", transform: transformers.toLocalPhone },
  { queryParam: "dateOfBirth", formField: "dob" },
  { queryParam: "maritalStatus", formField: "maritalStatus", transform: transformers.capitalize },
  { queryParam: "gender", formField: "gender", transform: transformers.capitalize },
  { queryParam: "address", formField: "address" },

  // Next of Kin
  { queryParam: "nextOfKinName", formField: "nextOfKin" },
  { queryParam: "nextOfKinPhone", formField: "nextOfKinPhone", transform: transformers.toLocalPhone },

  // Business Information
  { queryParam: "businessName", formField: "businessName" },
  { queryParam: "businessDescription", formField: "businessNature" },

  // TIN Information
  { queryParam: "tinNumber", formField: "tin" },
  { queryParam: "tinType", formField: "tinType", transform: transformers.normalizeTinType },

  // Loan Information
  { queryParam: "amount", formField: "loanAmount" },
];

/**
 * Alternative hook that builds a data object and uses reset() instead of setValue().
 * Useful for forms that prefer to reset all values at once.
 *
 * @param searchParams - URLSearchParams from useSearchParams
 * @param reset - reset function from react-hook-form
 * @param fieldMappings - Array of field mapping configurations
 */
export const useFormPrepopulationWithReset = (
  searchParams: URLSearchParams,
  reset: UseFormReset<any>,
  fieldMappings: FieldMapping[]
) => {
  useEffect(() => {
    const formData: Record<string, any> = {};
    let hasAnyValue = false;

    fieldMappings.forEach(({ queryParam, formField, transform = transformers.identity }) => {
      // Handle combined fields (e.g., fullName from firstName + middleName + lastName)
      if (Array.isArray(queryParam)) {
        const values = queryParam.map(param => searchParams.get(param) || "");
        const hasValues = values.some(v => v);

        if (hasValues) {
          const combinedValue = transformers.combineNames(values);
          if (combinedValue) {
            formData[formField] = combinedValue;
            hasAnyValue = true;
          }
        }
      } else {
        // Handle single field mapping
        const value = searchParams.get(queryParam);
        if (value) {
          const transformedValue = transform(value);

          // Handle transformers that return objects (e.g., splitPhone)
          if (typeof transformedValue === "object") {
            Object.assign(formData, transformedValue);
          } else {
            formData[formField] = transformedValue;
          }
          hasAnyValue = true;
        }
      }
    });

    // Only reset if we have at least one value to populate
    if (hasAnyValue) {
      console.log("Prepopulating form with:", formData);
      reset(formData);
    }
  }, [searchParams, reset, fieldMappings]);
};

/**
 * Predefined field mappings for Oze Ghana/Nigeria form (LoanForm)
 */
export const ozeGhanaFieldMappings: FieldMapping[] = [
  // Personal Information
  { queryParam: ["firstName", "middleName", "lastName"], formField: "fullName" },
  { queryParam: "email", formField: "email" },
  { queryParam: "phoneNo", formField: "phone", transform: transformers.splitPhone }, // Returns {phoneCode, phone}
  { queryParam: "dateOfBirth", formField: "dateOfBirth" },
  { queryParam: "maritalStatus", formField: "maritalStatus", transform: transformers.normalizeMaritalStatus },
  { queryParam: "gender", formField: "gender", transform: transformers.normalizeGender },
  { queryParam: "levelOfEducation", formField: "levelOfEducation", transform: transformers.normalizeEducation },

  // Possessions & Housing
  { queryParam: "address", formField: "address" },
  { queryParam: "numOfCellPhonesOwned", formField: "numOfCellPhonesOwned" },
  { queryParam: "numOfChildren", formField: "numOfChildren" },
  { queryParam: "ownsACar", formField: "ownsACar", transform: transformers.normalizeYesNo },
  { queryParam: "carManufacturingYear", formField: "carManufacturingYear" },
  { queryParam: "periodUsingCellPhoneText", formField: "periodUsingCellPhoneText", transform: transformers.normalizeCellPhonePeriod },
  { queryParam: "housingSituationText", formField: "housingSituationText", transform: transformers.normalizeHousingSituation },

  // Additional Questions
  { queryParam: "whatsappType", formField: "whatsappType", transform: transformers.normalizeWhatsappType },
  { queryParam: "numFinancialSupport", formField: "numFinancialSupport" },

  // Next of Kin
  { queryParam: "nextOfKinName", formField: "nextOfKinName" },
  { queryParam: "nextOfKinPhone", formField: "nextOfKinPhone", transform: (value: string) => {
    const split = transformers.splitPhone(value);
    return { nextOfKinPhoneCode: split.phoneCode, nextOfKinPhone: split.phone };
  }},

  // Business Information
  { queryParam: "businessName", formField: "businessName" },
  { queryParam: "businessDescription", formField: "businessDescription" },
  { queryParam: "businessAddress", formField: "businessAddress" },
  { queryParam: "businessLocation", formField: "businessLocation" },
  { queryParam: "yearBusinessStarted", formField: "yearBusinessStarted" },
  { queryParam: "numberOfEmployees", formField: "numberOfEmployees" },

  // TIN & Additional
  { queryParam: "tinNumber", formField: "tinNumber" },
  { queryParam: "tinType", formField: "tinType", transform: transformers.normalizeTinType },
  { queryParam: "tradeAssociation", formField: "tradeAssociation" },
  { queryParam: "periodWithNoIncome", formField: "periodWithNoIncome" },

  // Financial Information
  { queryParam: "currentBankBalance", formField: "currentBankBalance" },
  { queryParam: "currentMomoBalance", formField: "currentMomoBalance" },
  { queryParam: "hasOtherBusiness", formField: "hasOtherBusiness", transform: transformers.normalizeYesNo },

  // Loan Information
  { queryParam: "amount", formField: "amount" },
  { queryParam: "reasons", formField: "reasons" },
  { queryParam: "otherReasonForLoanText", formField: "otherReasonForLoanText" },
];

export { transformers };
